import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { CaseDateRange, ReportingKitsTable } from '../../../components/reporting';
import { LineChart } from '../../../components/charts';
import LoadScreen from '../../../components/load-screen';

import { getSurgeons } from '../../../actions/surgeonsActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import { getKits } from '../../../actions/kitsActions';
import { getCasesWithSetsAllocation } from '../../../actions/casesActions';

import { prepareCasesDataMonthly, prepareCasesDataDaily, prepareCasesDataQuarterly } from '../../../utils/analytics';

import { presets } from '../../../constants/analytics';
import { onPresetChange as setPresetDates } from '../../../utils/date';

import './reporting-page.scss';

const presetOptions = [
  { label: 'This month', value: presets.THIS_MONTH },
  { label: 'This quarter', value: presets.THIS_QUARTER },
  { label: 'This year', value: presets.THIS_YEAR },
  { label: 'Last month', value: presets.LAST_MONTH },
  { label: 'Last quarter', value: presets.LAST_QUARTER },
  { label: 'Last year', value: presets.LAST_YEAR },
];

const filteredFields = ['surgeonName', 'hospitalName', 'kitName'];
const getInitialFilters = () => {
  const initialFilter = {};

  filteredFields?.forEach((field) => {
    initialFilter[field] = [];
  });

  return initialFilter;
};

const getPresetRange = (value, yearAgo = false) => {
  let minDate;
  let maxDate;

  setPresetDates(value, {
    onMinDateChange: (date) => { minDate = date; },
    onMaxDateChange: (date) => { maxDate = date; },
  }, yearAgo);

  return { minDate, maxDate };
};

const KitReportingPage = () => {
  const dispatch = useDispatch();
  const tenantPrimaryColor = useSelector((state) => state.tenant.currentTenant.colorPrimary);
  const tenantId = useSelector((state) => state.tenant.currentTenant.id);
  const surgeons = useSelector((state) => state.surgeons.list);
  const hospitals = useSelector((state) => state.hospitals.list);
  const kits = useSelector((state) => state.kits.list);

  const [loading, setLoading] = useState(false);

  const [sets, setSets] = useState([]);

  const [preset, setPreset] = useState(presets.THIS_MONTH);
  const [dateRange, setDateRange] = useState(() => getPresetRange(presets.THIS_MONTH));
  const { minDate, maxDate } = dateRange;

  const [presetCompare, setPresetCompare] = useState('');

  const [filter, setFilter] = useState(getInitialFilters());

  const compareRange = useMemo(() => {
    if (!presetCompare) {
      return null;
    }

    const comparePreset = {
      [presets.THIS_MONTH]: presets.LAST_MONTH,
      [presets.THIS_QUARTER]: presets.LAST_QUARTER,
      [presets.THIS_YEAR]: presets.LAST_YEAR,
    }[preset];

    return comparePreset ? getPresetRange(comparePreset, presetCompare === presets.YEAR_AGO) : null;
  }, [preset, presetCompare]);
  const minDateCompare = compareRange?.minDate;
  const maxDateCompare = compareRange?.maxDate;
  const minDateValue = minDate?.valueOf();
  const maxDateValue = maxDate?.valueOf();
  const minDateCompareValue = minDateCompare?.valueOf();
  const maxDateCompareValue = maxDateCompare?.valueOf();

  useEffect(() => {
    if (!tenantId) {
      return;
    }

    Promise.all([
      dispatch(getKits()),
      dispatch(getSurgeons()),
      dispatch(getHospitals()),
    ]).catch((err) => console.error(err));
  }, [dispatch, tenantId]);

  useEffect(() => {
    if (!tenantId || !minDate || !maxDate || minDateValue >= maxDateValue) {
      setSets([]);
      setLoading(false);
      return;
    }

    let active = true;
    const dateRanges = [dateRange];
    if (compareRange) {
      dateRanges.push(compareRange);
    }

    setSets([]);
    setLoading(true);
    dispatch(getCasesWithSetsAllocation({ dateRanges }))
      .then((cases) => {
        if (!active) {
          return;
        }

        const list = [];
        cases.forEach((caseItem) => {
          caseItem.setsAllocation?.forEach((allocation) => {
            if (allocation.shippingDate && allocation.kit) {
              list.push({
                ...allocation,
                id: `${caseItem.id}_${allocation.kit}`,
                hospital: caseItem.hospital,
                surgeon: caseItem.surgeon,
                kit: allocation.kit,
                date: allocation.shippingDate,
                quantity: allocation.quantity,
                caseId: caseItem.id,
                patientReference: caseItem.patientReference,
              });
            }
          });
        });

        setSets(list);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => { active = false; };
  }, [dispatch, tenantId, minDateValue, maxDateValue, minDateCompareValue, maxDateCompareValue]);

  const handlePresetChange = (value) => {
    setPreset(value);
    setPresetCompare('');
    setDateRange(getPresetRange(value));
  };

  const handleDateChange = (field, date) => {
    setDateRange((current) => ({
      ...current,
      [field]: date ? moment(date)[field === 'minDate' ? 'startOf' : 'endOf']('day') : null,
    }));
    setPreset(null);
    setPresetCompare('');
  };

  const getChartData = (filteredSets, min, max) => {
    if (min.month() === max.month() && min.year() === max.year()) {
      return prepareCasesDataDaily(filteredSets, min.daysInMonth());
    }

    if (min.quarter() === max.quarter() && min.year() === max.year()) {
      return prepareCasesDataQuarterly(filteredSets, min.quarter());
    }

    return prepareCasesDataMonthly(filteredSets);
  };

  const handleFilterChange = (field, values) => {
    setFilter({ ...filter, [field]: values });
  };

  const getFilterOptions = () => {
    const hospitalsOptions = [];
    const kitsOptions = [];
    const surgeonsOptions = [];

    sets?.forEach((item) => {
      const { hospital, kit, surgeon } = item;

      if (hospital && !hospitalsOptions?.map((item) => item.id)?.includes(hospital)) {
        const hospitalObj = hospitals?.find((item) => item.id === hospital);
        if (hospitalObj) {
          hospitalsOptions?.push(hospitalObj);
        }
      }

      if (kit && !kitsOptions?.map((item) => item.id)?.includes(kit)) {
        const procedureObj = kits?.find((item) => item.id === kit);
        if (procedureObj) {
          kitsOptions?.push(procedureObj);
        }
      }

      if (surgeon && !surgeonsOptions?.map((item) => item.id)?.includes(surgeon)) {
        const surgeonObj = surgeons?.find((item) => item.id === surgeon);
        if (surgeonObj) {
          surgeonsOptions?.push(surgeonObj);
        }
      }
    });

    return { hospitalName: hospitalsOptions, kitName: kitsOptions, surgeonName: surgeonsOptions };
  };

  const handleRemoveFilterItem = (key, value) => {
    setFilter({ ...filter, [key]: filter[key].filter((item) => item !== value) });
  };

  // Memoized data

  const filteredSets = useMemo(() => (!minDate || !maxDate ? [] : sets.filter((item) => {
    let persistence = true;

    Object.keys(filter)
      .forEach((key) => {
        const field = key.split('Name')[0];
        if (filter[key].length && !filter[key].includes(item[field])) {
          persistence = false;
        }
      });

    return persistence && minDate.isBefore(item.date) && maxDate.isAfter(item.date);
  })), [sets, minDate, maxDate, filter]);

  const casesChartData = useMemo(() => {
    if (!minDate || !maxDate) {
      return [];
    }

    const data = getChartData(filteredSets, minDate, maxDate);

    if (compareRange) {
      const comparedSets = sets.filter((item) => minDateCompare.isBefore(item.date) && maxDateCompare.isAfter(item.date));
      const compareData = getChartData(comparedSets, minDateCompare, maxDateCompare);

      if (preset === presets.THIS_QUARTER && presetCompare === presets.PREVIOUS_PERIOD) {
        const previousQuarter = compareData?.map((item) => ({
          data: item.data?.map((i, index) => ({ x: data[0]?.data[index]?.x, y: i.y })),
          id: `${item.id}_compare`
        }));
        return [...previousQuarter, ...data];
      }

      return [...compareData?.map((item) => ({ ...item, id: `${item.id}_compare` })), ...data];
    }

    return data;
  }, [sets, filteredSets, minDate, maxDate, minDateCompare, maxDateCompare, compareRange, preset, presetCompare]);

  const presetCompareOptions = useMemo(() => {
    const options = [
      { label: 'Don\'t compare', value: '' }
    ];

    if ([presets.THIS_YEAR, presets.THIS_QUARTER, presets.THIS_MONTH].includes(preset)) {
      options?.push({ label: 'Previous period', value: presets.PREVIOUS_PERIOD });
    }

    if ([presets.THIS_QUARTER, presets.THIS_MONTH].includes(preset)) {
      options?.push({ label: 'Year ago', value: presets.YEAR_AGO });
    }

    return options;
  }, [preset]);

  return (
    <div className='page-container reporting-page__container'>
      {loading && <LoadScreen />}
      <div className='page-title'>Kits Report</div>
      <div className='reporting-page__body'>
        <div className='case-reporting-chart__container'>
          <div className='case-reporting-chart'>
            <LineChart
              data={casesChartData}
              colors={casesChartData.length > 1 ? ['var(--app-text-subtle)', tenantPrimaryColor] : tenantPrimaryColor}
              margin={{ top: 20, right: 15, bottom: 30, left: 30 }}
              entity='kit'
            />
          </div>
          <CaseDateRange
            minDate={minDate}
            onMinDateChange={(date) => handleDateChange('minDate', date)}
            maxDate={maxDate}
            onMaxDateChange={(date) => handleDateChange('maxDate', date)}
            preset={preset}
            onPresetChange={handlePresetChange}
            presetCompare={presetCompare}
            onPresetChangeCompare={setPresetCompare}
            options={presetOptions}
            compareOptions={presetCompareOptions}
          />
        </div>

        <ReportingKitsTable
          sets={filteredSets}
          surgeons={surgeons}
          hospitals={hospitals}
          kits={kits}
          filter={filter}
          filterOptions={getFilterOptions()}
          onFilterChange={handleFilterChange}
          onRemoveFilter={handleRemoveFilterItem}
        />
      </div>
    </div>
  );
};

export default KitReportingPage;
